import { useCallback, useMemo, useState, useEffect } from 'react';
import debounce from 'lodash/debounce';
import moment from 'moment';

const isDateInRange = (leadDate, selectedDate) => {
  if (!selectedDate) return true;

  const leadMoment = moment(leadDate);

  // Check if selectedDate is a daily format (YYYY-MM-DD) or monthly (YYYY-MM)
  if (selectedDate.length === 10) {
    // Daily format - compare exact date
    return leadMoment.format('YYYY-MM-DD') === selectedDate;
  } else {
    // Monthly format - compare month and year
    return leadMoment.format('YYYY-MM') === selectedDate;
  }
};

export const useLeadsFilter = (columns, searchQuery, selectedCadenceData, selectedMonth) => {
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(searchQuery);

  // Create a dependency that changes when leads are added/updated
  const leadsHash = useMemo(() => {
    if (!columns || columns.length === 0) return '';
    
    // Create a hash based on lead IDs and their update timestamps
    return columns.reduce((hash, column) => {
      const columnLeads = column.data || [];
      const columnHash = columnLeads.reduce((colHash, lead) => {
        return colHash + `${lead.id}-${lead.updated_at || lead.created_at}-`;
      }, '');
      return hash + columnHash;
    }, '');
  }, [columns]);

  // Debounce the search query
  useEffect(() => {
    const debounced = debounce(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 300);
    
    debounced();
    return () => debounced.cancel();
  }, [searchQuery]);

  // Filter leads by search query
  const filterLeads = useCallback((leads) => {
    if (!debouncedSearchQuery) return leads;
    const filtered = leads.filter((lead) =>
      Object.values(lead).some((value) =>
        value && value.toString().toLowerCase().includes(debouncedSearchQuery.toLowerCase())
      )
    );
    return filtered;
  }, [debouncedSearchQuery]);

  // Memoize the filtered data
  const filteredData = useMemo(() => {
    if (!columns || columns.length === 0) return [];
    return columns.reduce((acc, column) => acc.concat(filterLeads(column.data)), []);
  }, [columns, filterLeads, leadsHash]);

  // Helper to filter leads for a column
  const getFilteredColumnLeads = useCallback((column) => {
    // First filter by search query
    var filteredData = filterLeads(column.data);

    // Special filtering for the "Leads" column (id: 2)
    if (column.id === 2 && selectedCadenceData && selectedCadenceData.name) {
      filteredData = filteredData.filter(function(lead) {
        // Filter by selected date if provided
        if (!isDateInRange(lead.created_at, selectedMonth)) return false;

        // Check if lead has cadences assigned
        if (!lead.additional_data || !lead.additional_data.cadences) return false;

        // Check if lead has the selected cadence assigned
        var hasSelectedCadence = lead.additional_data.cadences.some(function(cadence) {
          return cadence.name === selectedCadenceData.name;
        });

        // Only show leads that have the selected cadence assigned
        return hasSelectedCadence;
      });
    }

    // Filter other columns by selected cadence (same logic as Summary component)
    // BUT: Don't filter the "Inicio" column (id: 1) - it should show all leads
    // AND: Don't filter step columns - they should show leads that completed the specific task
    if (selectedCadenceData && selectedCadenceData.name && column.id !== 1 && column.id !== 2 && !column.id.toString().startsWith('step_')) {
      filteredData = filteredData.filter(function(lead) {
        // Filter by selected date if provided
        if (!isDateInRange(lead.created_at, selectedMonth)) return false;

        // Check if lead has the selected cadence assigned
        if (!lead.additional_data || !lead.additional_data.cadences) return false;

        return lead.additional_data.cadences.some(function(cadence) {
          return cadence.name === selectedCadenceData.name;
        });
      });
    }

    return filteredData;
  }, [filterLeads, selectedCadenceData, selectedMonth]);

  // Memoize the filtered columns with merged Inicio + Leads
  const filteredDataToColumns = useMemo(function() {
    var result = [];
    var hasAddedMergedLeadsColumn = false;

    columns.forEach(function(column) {
      // Skip column 2 - we merge it with column 1
      if (column.id === 2) {
        return;
      }

      // Merge columns 1 and 2 into a single "Leads" column
      if (column.id === 1) {
        if (!hasAddedMergedLeadsColumn) {
          // Get leads from column 1 (Inicio)
          var inicioLeads = getFilteredColumnLeads(column);

          // Get leads from column 2 (Leads) if it exists
          var leadsColumn = columns.find(function(c) { return c.id === 2; });
          var leadsLeads = leadsColumn ? getFilteredColumnLeads(leadsColumn) : [];

          // Merge both columns
          var mergedLeads = inicioLeads.concat(leadsLeads);

          result.push({
            id: column.id,
            name: 'Leads',
            style: column.style,
            type: column.type,
            data: mergedLeads,
            total: mergedLeads.length
          });
          hasAddedMergedLeadsColumn = true;
        }
        return;
      }

      // Other columns - normal processing
      var columnLeads = getFilteredColumnLeads(column);

      result.push({
        id: column.id,
        name: column.name,
        style: column.style,
        type: column.type,
        stepData: column.stepData,
        data: columnLeads,
        total: columnLeads.length
      });
    });

    return result;
  }, [columns, getFilteredColumnLeads, leadsHash]);

  return {
    filteredData,
    filteredDataToColumns
  };
}; 